import { useCallback, useEffect, useRef } from 'react';
import * as chatApi from '../../../api/chat';

export function useChatWs(token: string | null, onEvent: (e: any) => void) {
  const wsRef = useRef<WebSocket | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  const hbTimer = useRef<ReturnType<typeof setInterval>>();
  const onEventRef = useRef(onEvent);
  const retryCount = useRef(0);

  useEffect(() => { onEventRef.current = onEvent; });

  const connect = useCallback(() => {
    if (!token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(chatApi.getChatWsUrl(token));
    wsRef.current = ws;

    ws.onopen = () => {
      retryCount.current = 0;
      clearTimeout(retryTimer.current);
      // Start heartbeat
      hbTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'heartbeat' }));
        }
      }, 30_000);
    };

    ws.onmessage = (e) => {
      try { onEventRef.current(JSON.parse(e.data)); } catch {}
    };

    ws.onclose = () => {
      clearInterval(hbTimer.current);
      const delay = Math.min(1000 * 2 ** retryCount.current, 30_000);
      retryCount.current += 1;
      retryTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, [token]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(retryTimer.current);
      clearInterval(hbTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return useCallback((data: object) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data));
  }, []);
}
